<template>
  <ElevationPage
    class="sm:max-w-3xl"
    v-model:selectedLocation="selectedLocation"
    :currentLocationSupported="false"
    :location="locationWithElevation"
    :error="elevation?.error"
    :locationError="locationError"
    :loading="elevation?.pending || true"
    path="/map"
  >
    <template #title>
      <div flex flex-col sm:flex-row gap-2>
        <p font-slab text-xl sm:text-2xl text-brand-orange><NuxtLink to="/">What is my elevation?</NuxtLink></p>
        <h1 text-xl sm:text-2xl font-500 text-stone-500>Elevation Map</h1>
      </div>
    </template>
    <template #elevation="{ locationFormatted, elevationSources, elevationSourcesPending, showElevationSource }">
      <div>
        <div relative w-full h-lg bg-stone-100 bg-opacity-50>
          <LazyMapContour
            v-if="lastLocation"
            :marker="{ color: '#FC7A24' }"
            :units="locationFormatted.elevationUnits.value.name"
            :options="{
              zoom: 14,
              center: [lastLocation.longitude, lastLocation.latitude],
              style: '/styles/elevation-color.json',
              pitch: 30,
            }"
            @markerMove="setMapLocation"
            @load="mapLoaded = true"
          />
          <transition enter-from-class="translate-y-10 scale-80 opacity-0" enter-active-class="transition duration-300">
            <div
              class="my-2 absolute bottom-0 left-0 m-2 p-2 px-4 rounded-3 bg-orange-50 bg-opacity-40 backdrop-blur-sm shadow shadow-neutral-400"
              v-if="lastLocation && locationFormatted.elevation.value && mapLoaded"
            >
              <div font-mono text-stone-700 font-600 lh-snug text-lg>Elevation</div>
              <p text-5xl sm:text-6xl font-bold>
                {{ locationFormatted.elevation.value }} {{ locationFormatted.elevationUnits.value.name }}
              </p>
            </div>
          </transition>
        </div>
        <!-- caption strip attached to the bottom edge of the map -->
        <ElevationSource
          v-if="showElevationSource && (elevationSourcesPending || elevationSources?.length)"
          class="px-3 py-2 bg-stone-100 border-t border-stone-200"
          :sources="elevationSources"
          :pending="elevationSourcesPending"
          :elevationUnits="locationFormatted.elevationUnits.value"
        />
      </div>
    </template>
    <template #below-info>
      <WikipediaInfo v-if="placeName" :slug="placeName" />
    </template>
  </ElevationPage>
</template>
<script setup>
const elevation = ref();
const mapLoaded = ref(false);
// where the map is centered; it stays put while the next location loads
const lastLocation = ref(null);

const { selectedLocation, coordinates, placeName, locationError, setMapLocation, onCoordinatesChange } =
  useSelectedLocation();

const locationWithElevation = computed(() => {
  return {
    ...coordinates.value,
    altitude: elevation.value?.data,
    altitudeSource: "web",
  };
});

onCoordinatesChange(async (location) => {
  lastLocation.value = location;
  elevation.value = await useFetchElevation(location);
});

const page = await useContent("/map");

useSeoMeta({
  title: () => {
    if (placeName.value) {
      return `${placeName.value} Elevation Map - What is my elevation?`;
    }
    return `Elevation Map with Contour Lines - What is my elevation?`;
  },
  description: page.value.description,
  ogDescription: page.value.description,
});

defineOgImage({
  title: "Elevation Map",
  subtitle: "Find the elevation of your current location, or any point on Earth.",
});
</script>
